// Resolves a list of selected ids into the option objects react-select renders
// as chips. Walking `ids` in order — rather than listing matched options first
// and unmatched ids after — keeps each chip where its id sits, so Backspace
// removes the most recent pick (VEN-1811). Ids with no matching option get a
// placeholder so they survive selection changes instead of being dropped.
//
// A placeholder is its own type rather than a `T`: callers hand in rich
// records (a whole Venue, say) that only `id` and `name` are ever read from,
// so a synthesized stand-in cannot honestly claim to be one. Returning
// `(T | Placeholder)[]` says that outright instead of casting it away.
export function resolveSelection<Id extends string, T, Placeholder = T>(
  ids: readonly Id[],
  options: readonly T[],
  getId: (option: T) => Id,
  placeholderFor: (id: Id) => Placeholder
): (T | Placeholder)[] {
  const seen = new Set<Id>();

  return ids.flatMap(id => {
    if (seen.has(id)) return [];
    seen.add(id);

    return [options.find(option => getId(option) === id) ?? placeholderFor(id)];
  });
}
